import { useEffect, useState } from 'react';

/**
 * useCountUp — smoothly animates a number from `from` to `to`.
 * Uses requestAnimationFrame. For small deltas (≤ 20) we use linear easing so
 * the number visibly ticks through every integer over the full duration.
 * For larger deltas we use ease-out-cubic so the animation feels natural.
 *
 * @param {number} to        Final value to reach
 * @param {object} opts
 * @param {number} opts.duration   Total animation time in ms (default 1400)
 * @param {number} opts.from       Start value (default 0)
 * @param {any}    opts.trigger    Optional dep that re-runs the animation when it changes
 * @returns {number} The current animated value (integer)
 */
export function useCountUp(to, opts = {}) {
    const { duration = 1400, from = 0, trigger } = opts;
    const target = Number(to) || 0;
    const [value, setValue] = useState(from);

    useEffect(() => {
        if (target === from) { setValue(target); return undefined; }

        const delta = Math.abs(target - from);
        // Small numbers get linear easing so each integer is visible for a moment
        // (ease-out-cubic causes small targets to snap to their final value too early).
        const linear = delta <= 20;
        const ease = linear ? (t) => t : (t) => 1 - Math.pow(1 - t, 3);

        let raf;
        const start = performance.now();
        const step = (now) => {
            const t = Math.min(1, (now - start) / duration);
            if (t >= 1) {
                setValue(target);
                return;
            }
            setValue(Math.floor(from + (target - from) * ease(t)));
            raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [target, duration, from, trigger]);

    return value;
}
